import { tanggalWit } from './wit'

/**
 * Partial dates (spec 8.1): a date that may be incomplete is stored as text matching
 * `YYYY`, `YYYY-MM` or `YYYY-MM-DD`. Sorting is lexicographic, which the canonical form
 * guarantees. Age calculations use whatever precision exists.
 *
 * The forms accept what the hint describes, "12-05-1987, 05-1987, 1987" (day-month-year,
 * the Indonesian reading order), and store the canonical form.
 */

export type Presisi = 'tahun' | 'bulan' | 'hari'

export interface PartialDateParts {
  readonly tahun: number
  readonly bulan?: number
  readonly hari?: number
}

export const PARTIAL_DATE_PATTERN = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/

function daysInMonth(tahun: number, bulan: number): number {
  return new Date(Date.UTC(tahun, bulan, 0)).getUTCDate()
}

/** Canonical text to parts, or null when the text is not a valid partial date. */
export function parsePartialDate(text: string): PartialDateParts | null {
  const match = PARTIAL_DATE_PATTERN.exec(text)
  if (match === null) return null
  const tahun = Number(match[1])
  if (tahun < 1000) return null
  if (match[2] === undefined) return { tahun }
  const bulan = Number(match[2])
  if (bulan < 1 || bulan > 12) return null
  if (match[3] === undefined) return { tahun, bulan }
  const hari = Number(match[3])
  if (hari < 1 || hari > daysInMonth(tahun, bulan)) return null
  return { tahun, bulan, hari }
}

export function isPartialDate(value: unknown): boolean {
  return typeof value === 'string' && parsePartialDate(value) !== null
}

export function formatPartialDate(parts: PartialDateParts): string {
  const tahun = String(parts.tahun).padStart(4, '0')
  if (parts.bulan === undefined) return tahun
  const bulan = String(parts.bulan).padStart(2, '0')
  if (parts.hari === undefined) return `${tahun}-${bulan}`
  return `${tahun}-${bulan}-${String(parts.hari).padStart(2, '0')}`
}

export function partialDatePrecision(text: string): Presisi {
  const parts = parsePartialDate(text)
  if (parts === null) throw new RangeError(`Not a partial date: "${text}"`)
  if (parts.hari !== undefined) return 'hari'
  if (parts.bulan !== undefined) return 'bulan'
  return 'tahun'
}

/**
 * Parse what a person types: `12-05-1987`, `05-1987`, `1987`, with `-`, `/`, `.` or a
 * space as separator, or an already canonical `1987-05-12`. Returns canonical text or
 * null. Two-digit years are not guessed.
 */
export function parsePartialDateInput(input: string): string | null {
  const trimmed = input.trim()
  if (isPartialDate(trimmed)) return trimmed
  const parts = trimmed.split(/[-/. ]+/)
  if (parts.some((p) => !/^\d+$/.test(p))) return null
  const tahun = parts.at(-1)
  if (tahun === undefined || tahun.length !== 4) return null
  let canonical: string
  if (parts.length === 1) canonical = tahun
  else if (parts.length === 2) canonical = `${tahun}-${parts[0]?.padStart(2, '0')}`
  else if (parts.length === 3) {
    canonical = `${tahun}-${parts[1]?.padStart(2, '0')}-${parts[0]?.padStart(2, '0')}`
  } else return null
  return isPartialDate(canonical) ? canonical : null
}

/**
 * Canonical text to the typed form the hint describes: `1987-05-12` becomes `12-05-1987`,
 * `1987-05` becomes `05-1987`, `1987` stays. Unreadable text and empty input pass through
 * unchanged so a form shows what it holds.
 */
export function formatPartialDateInput(canonical: string | null | undefined): string {
  if (canonical === null || canonical === undefined || canonical === '') return ''
  const parts = parsePartialDate(canonical)
  if (parts === null) return canonical
  const pad = (n: number) => String(n).padStart(2, '0')
  if (parts.bulan === undefined) return String(parts.tahun)
  if (parts.hari === undefined) return `${pad(parts.bulan)}-${parts.tahun}`
  return `${pad(parts.hari)}-${pad(parts.bulan)}-${parts.tahun}`
}

/** Lexicographic order, which is also chronological for canonical text. */
export function comparePartialDates(a: string, b: string): -1 | 0 | 1 {
  if (a === b) return 0
  return a < b ? -1 : 1
}

/** The earliest full date the partial date can mean, as canonical `YYYY-MM-DD`. */
export function partialDateEarliest(text: string): string {
  const parts = parsePartialDate(text)
  if (parts === null) throw new RangeError(`Not a partial date: "${text}"`)
  return formatPartialDate({ tahun: parts.tahun, bulan: parts.bulan ?? 1, hari: parts.hari ?? 1 })
}

/** The latest full date the partial date can mean, as canonical `YYYY-MM-DD`. */
export function partialDateLatest(text: string): string {
  const parts = parsePartialDate(text)
  if (parts === null) throw new RangeError(`Not a partial date: "${text}"`)
  const bulan = parts.bulan ?? 12
  return formatPartialDate({
    tahun: parts.tahun,
    bulan,
    hari: parts.hari ?? daysInMonth(parts.tahun, bulan),
  })
}

export function toPartialDate(date: Date): string {
  return formatPartialDate({
    tahun: date.getUTCFullYear(),
    bulan: date.getUTCMonth() + 1,
    hari: date.getUTCDate(),
  })
}

/** True when every reading of the partial date lies after `today`. */
export function isFuturePartialDate(text: string, today: string | Date): boolean {
  // An instant is read as its WIT calendar date (dates/wit.ts), never the UTC one.
  const reference = typeof today === 'string' ? partialDateLatest(today) : (tanggalWit(today) ?? '')
  return partialDateEarliest(text) > reference
}

/**
 * The last day a date may lie ahead (release 17, owner 2026-10-08: the parish prepares letters
 * and certificates before the ceremony): the same day next year, the last of February when that
 * day is 29 February. `hariIni` is canonical `YYYY-MM-DD`.
 */
export function batasTanggalDepan(hariIni: string): string {
  const parts = parsePartialDate(hariIni)
  if (parts === null || parts.bulan === undefined || parts.hari === undefined) {
    throw new RangeError(`Not a full date: "${hariIni}"`)
  }
  const tahun = parts.tahun + 1
  return formatPartialDate({
    tahun,
    bulan: parts.bulan,
    hari: Math.min(parts.hari, daysInMonth(tahun, parts.bulan)),
  })
}

/**
 * True when the date lies further ahead than a year (`batasTanggalDepan`): its first day comes
 * after the limit, so "2027" and "10-2027" are a year ahead of 8 October 2026, "2028" more. An
 * instant is read as its WIT calendar date; an unreadable today refuses nothing.
 */
export function tanggalTerlaluJauh(text: string, today: string | Date): boolean {
  const hari = typeof today === 'string' ? today : tanggalWit(today)
  if (hari === null || hari.length !== 10 || !isPartialDate(hari)) return false
  return partialDateEarliest(text) > batasTanggalDepan(hari)
}

export interface Usia {
  /** Completed years at the precision available. */
  readonly tahun: number
  /** The coarser of the two precisions involved. */
  readonly presisi: Presisi
}

/**
 * Age at `pada` (default: today) for someone born on `lahir`, at whatever precision
 * exists. With full dates it is the usual completed-years count. With months only, the
 * birthday counts as reached in the birth month. With years only, it is the difference of
 * the years. Returns null when either date is not a valid partial date.
 */
export function usiaPada(lahir: string, pada: string | Date = new Date()): Usia | null {
  const born = parsePartialDate(lahir)
  const at = parsePartialDate(typeof pada === 'string' ? pada : (tanggalWit(pada) ?? ''))
  if (born === null || at === null) return null
  let tahun = at.tahun - born.tahun
  if (born.bulan === undefined || at.bulan === undefined) return { tahun, presisi: 'tahun' }
  if (born.hari === undefined || at.hari === undefined) {
    if (at.bulan < born.bulan) tahun -= 1
    return { tahun, presisi: 'bulan' }
  }
  if (at.bulan < born.bulan || (at.bulan === born.bulan && at.hari < born.hari)) tahun -= 1
  return { tahun, presisi: 'hari' }
}

const BULAN = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
] as const

const BULAN_PENDEK = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Mei',
  'Jun',
  'Jul',
  'Agu',
  'Sep',
  'Okt',
  'Nov',
  'Des',
] as const

/**
 * Human form for screens: `12 Mei 1987`, `Mei 1987` or `1987` (`pendek` gives `12 Mei
 * 1987` too; the long month names are for printed letters: `12 Mei 1987` reads the same,
 * `28 September 2026` differs from `28 Sep 2026`).
 */
export function formatPartialDateId(text: string, gaya: 'panjang' | 'pendek' = 'pendek'): string {
  const parts = parsePartialDate(text)
  if (parts === null) return text
  if (parts.bulan === undefined) return String(parts.tahun)
  const bulan = (gaya === 'panjang' ? BULAN : BULAN_PENDEK)[parts.bulan - 1]
  if (parts.hari === undefined) return `${bulan} ${parts.tahun}`
  return `${parts.hari} ${bulan} ${parts.tahun}`
}
